import {
    API_BASE_URL,
    AUTH_CONTEXT,
    AUTH_SERVICE_BASE_URL,
    CONFIGURATION_CONTEXT,
    CONFIGURATION_SERVICE_BASE_URL,
    CONSENT_CONTEXT,
    CONSENT_SERVICE_BASE_URL,
    CUSTOMER_CONTEXT,
    CUSTOMER_SERVICE_BASE_URL,
    INVENTORY_CONTEXT,
    INVENTORY_SERVICE_BASE_URL,
    NOTIFICATION_CONTEXT,
    NOTIFICATION_SERVICE_BASE_URL,
    PAYMENT_CONTEXT,
    PAYMENT_SERVICE_BASE_URL,
    QUOTATION_CONTEXT,
    QUOTATION_SERVICE_BASE_URL,
    SHARED_CONTEXT,
    SHARED_SERVICE_BASE_URL,
    SHIPMENT_CONTEXT,
    SHIPMENT_SERVICE_BASE_URL,
    SP_MGMT_BASE_URL,
    SP_PROFILE_BASE_URL,
    SSO_REDIRECT_URI,
} from './envConfig';

const stripTrailingSlashes = (value: string) => value.replace(/\/+$/, '');

const normalizeContextPath = (value: string) => {
    const normalized = stripTrailingSlashes(value.trim());
    return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const stripConfigurationContext = (value?: string) =>
    value?.replace(/\/configuration-service\/?$/, '');

const resolveBaseUrl = (
    values: (string | undefined)[],
    transform?: (value?: string) => string | undefined,
): string => {
    for (const val of values) {
        const resolved = transform ? transform(val) : val;
        if (resolved) {
            return stripTrailingSlashes(resolved);
        }
    }
    throw new Error(`[config] Missing required base URL. All candidates are empty.`);
};

const resolveContextPath = (value: string): string => normalizeContextPath(value);

const buildApiUrl = (baseUrl: string, path: string) =>
    `${stripTrailingSlashes(baseUrl)}${path.startsWith('/') ? path : `/${path}`}`;

export const API_BASE_URLS = {
    shared: resolveBaseUrl([SHARED_SERVICE_BASE_URL, API_BASE_URL]),
    auth: resolveBaseUrl([AUTH_SERVICE_BASE_URL]),
    customer: resolveBaseUrl([CUSTOMER_SERVICE_BASE_URL]),
    shipment: resolveBaseUrl([SHIPMENT_SERVICE_BASE_URL]),
    quotation: resolveBaseUrl([QUOTATION_SERVICE_BASE_URL]),
    configuration: resolveBaseUrl(
        [CONFIGURATION_SERVICE_BASE_URL, SP_PROFILE_BASE_URL, SP_MGMT_BASE_URL],
        stripConfigurationContext,
    ),
    consent: resolveBaseUrl([CONSENT_SERVICE_BASE_URL]),
    payment: resolveBaseUrl([PAYMENT_SERVICE_BASE_URL]),
    partial: resolveBaseUrl([SHIPMENT_SERVICE_BASE_URL]),
    inventory: resolveBaseUrl([INVENTORY_SERVICE_BASE_URL]),
    notification: resolveBaseUrl([NOTIFICATION_SERVICE_BASE_URL, API_BASE_URL]),
} as const;

const API_CONTEXT_PATHS = {
    shared: resolveContextPath(SHARED_CONTEXT),
    authSso: resolveContextPath(AUTH_CONTEXT),
    customer: resolveContextPath(CUSTOMER_CONTEXT),
    shipment: resolveContextPath(SHIPMENT_CONTEXT),
    inventory: resolveContextPath(INVENTORY_CONTEXT),
    quotation: resolveContextPath(QUOTATION_CONTEXT),
    configuration: resolveContextPath(CONFIGURATION_CONTEXT),
    consent: resolveContextPath(CONSENT_CONTEXT),
    payment: resolveContextPath(PAYMENT_CONTEXT),
    partial: resolveContextPath(SHIPMENT_CONTEXT),
    notification: resolveContextPath(NOTIFICATION_CONTEXT),
} as const;

export const API_PATHS = {
    sso: {
        redirectUrl: `${API_CONTEXT_PATHS.authSso}/api/sso/redirect-url/azure`,
        callbackUrl: `${API_CONTEXT_PATHS.authSso}/api/sso/callback/azure`,
        refreshUrl: `${API_CONTEXT_PATHS.authSso}/api/sso/refresh`,
    },
    shared: {
        addressSuggestion: `${API_CONTEXT_PATHS.shared}/api/shared/addresses/suggestion`,
        addressValidate: `${API_CONTEXT_PATHS.shared}/api/shared/addresses/validate`,
        documentDescriptions: `${API_CONTEXT_PATHS.shared}/api/shared/document-descriptions`,
        prohibitedItems: `${API_CONTEXT_PATHS.shared}/api/shared/prohibited-items`,
        phoneValidation: `${API_CONTEXT_PATHS.shared}/api/shared/phone/validation`,
        dutiableCheck: `${API_CONTEXT_PATHS.shared}/api/shared/dutiable-check`,
        decodeQRCode: `${API_CONTEXT_PATHS.shared}/api/shared/qr-codes/_decode`,
        destinationAlert: `${API_CONTEXT_PATHS.shared}/api/shared/alerts`,
        sanctionedCountryAlert: `${API_CONTEXT_PATHS.shared}/api/shared/sanctioned-check`,
    },
    customer: {
        tokenGenerate: `${API_CONTEXT_PATHS.customer}/api/customers/token/generate`,
        validateAccount: `${API_CONTEXT_PATHS.customer}/api/customers/account`,
        searchCustomers: `${API_CONTEXT_PATHS.customer}/api/customers/_search`,
        esecureAuthRequired: `${API_CONTEXT_PATHS.customer}/api/customers/account/esecure-auth-required`,
        esecureEmailAuth: `${API_CONTEXT_PATHS.customer}/api/customers/account/esecure-email-auth`,
        esecureEmailOtpGenerate: `${API_CONTEXT_PATHS.customer}/api/customers/account/esecure-email-otp-generate`,
        esecureEmailOtpSubmit: `${API_CONTEXT_PATHS.customer}/api/customers/account/esecure-email-otp-submit`,
        identityOtpGenerate: `${API_CONTEXT_PATHS.customer}/api/customers/identity-otp-generate`,
        identityOtpSubmit: `${API_CONTEXT_PATHS.customer}/api/customers/identity-otp-submit`,
        consignees: `${API_CONTEXT_PATHS.customer}/api/consignees`,
        customers: `${API_CONTEXT_PATHS.customer}/api/customers`,
    },
    shipment: {
        validateDescription: `${API_CONTEXT_PATHS.shipment}/api/shipments/validation/description`,
        classifyHs: `${API_CONTEXT_PATHS.shipment}/api/shipments/classification/hs`,
        summarizeDescription: `${API_CONTEXT_PATHS.shipment}/api/shipments/description:summarize`,
        shipmentInit: `${API_CONTEXT_PATHS.shipment}/api/shipments`,
        shipmentFinalize: `${API_CONTEXT_PATHS.shipment}/api/shipments/_finalize`,
        shipmentHistory: {
            search: `${API_CONTEXT_PATHS.shipment}/api/shipments/_search`,
            detail: `${API_CONTEXT_PATHS.shipment}/api/shipments`,
            checkCancellationEligibility: (shpId: string, srvPntFcId: string) =>
                `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/cancellation-eligibility?srvPntFcId=${encodeURIComponent(srvPntFcId)}`,
            cancel: (shpId: string) =>
                `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/_cancel`,
            printDocuments: `${API_CONTEXT_PATHS.shipment}/api/shipments/documents/_print`,
        },
        checkOutStanding: (shpId: string) =>
            `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/_adc`,
        //shipmentHistorySearch: `${API_CONTEXT_PATHS.shipment}/api/shipments/_search`,
        //shipmentHistoryDetail: `${API_CONTEXT_PATHS.shipment}/api/shipments`,
        //shipmentHistoryDetail: `${API_CONTEXT_PATHS.shipment}/api/shipments`,
        partialLookup: `${API_CONTEXT_PATHS.shipment}/api/shipments/partial-shipments/_lookup`,
        querySA: `${API_CONTEXT_PATHS.shipment}/api/shipments/dropoff-shipments/_query`,
        publishSA: `${API_CONTEXT_PATHS.shipment}/api/shipments/checkpoints/sa/_publish`,
        printShpDoc: (shpId: string, type?: string) =>
            `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/documents/_print${type ? `?type=${encodeURIComponent(type)}` : ''}`,
        printShpDocDropoff: (shpId: string) =>
            `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/documents/_print`,
        viewInvImg: (shpId: string) =>
            `${API_CONTEXT_PATHS.shipment}/api/shipments/${shpId}/documents/inv/_view`,
        // Public, unauthenticated — customer-facing Shipment Receipt dynamic link download
        receiptDownload: `${API_CONTEXT_PATHS.shipment}/api/shipments/receipts/_download`,
    },
    inventory: {
        dashboardStats: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/dashboard-stats/_search`,
        shipmentInventorySearch: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/_search`,
        shipmentInventoryExtendHold: (shpId: string) =>
            `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/${shpId}/holding-extensions`,
        shipmentInventoryPickupScheduleRetries: (shpId: string) =>
            `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/${shpId}/pickup-schedule-retries`,
        courierHandover: {
            scanInValidate: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/scan-in/_validate`,
            scanOutValidate: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/scan-out/_validate`,
            scanInBatches: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/scan-in/batches`,
            scanOutBatches: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/scan-out/batches`,
        },
        customerCollection: {
            collectionLookup: `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/collections/_lookup`,
            confirmCollection: (shpId: string) =>
                `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/${shpId}/collections`,
            refuseCollection: (shpId: string) =>
                `${API_CONTEXT_PATHS.inventory}/api/shipment-inventorys/${shpId}/refusals`,
        },
    },
    payment: {
        submit: `${API_CONTEXT_PATHS.payment}/api/payments`,
        updateById: (shpPayId: string) => `${API_CONTEXT_PATHS.payment}/api/payments/${shpPayId}`,
        checkPaymentStatus: (shpPayId: string) =>
            `${API_CONTEXT_PATHS.payment}/api/payments/${shpPayId}`,
    },
    quotation: {
        generate: `${API_CONTEXT_PATHS.quotation}/api/quotations/generate`,
        retrieve: (qtnId: string) => `${API_CONTEXT_PATHS.quotation}/api/quotations/${qtnId}`,
        quotation: `${API_CONTEXT_PATHS.quotation}/api/quotations/quotation`,
        convertToShipment: `${API_CONTEXT_PATHS.quotation}/api/quotations/convert-to-shipment`,
        search: `${API_CONTEXT_PATHS.quotation}/api/quotations/_search`,
        quotationBase: `${API_CONTEXT_PATHS.quotation}/api/quotations`,
        printQuotationDoc: (qtnId: string) =>
            `${API_CONTEXT_PATHS.quotation}/api/quotations/${qtnId}/documents/_print`,
        shareQuotation: `${API_CONTEXT_PATHS.quotation}/api/quotations/_share`,
        saveSelectedShipmentOption: `${API_CONTEXT_PATHS.quotation}/api/quotations/quotation`,
    },
    configuration: {
        base: `${API_CONTEXT_PATHS.configuration}/api/configurations`,
        loginBase: `${API_CONTEXT_PATHS.configuration}/api/configurations/pre`,
        servicePoints: `${API_CONTEXT_PATHS.configuration}/api/configurations/service-points`,
        servicePointConfig: `${API_CONTEXT_PATHS.configuration}/api/configurations/service-point-config`,
        servicePointProfiles: `${API_CONTEXT_PATHS.configuration}/api/configurations/service-point-profiles`,
        uiLabelsBase: `${API_CONTEXT_PATHS.configuration}/api/configurations/ui-labels`,
        tariffs: `${API_CONTEXT_PATHS.configuration}/api/configurations/tariffs`,
        products: `${API_CONTEXT_PATHS.configuration}/api/configurations/products`,
        productsServices: `${API_CONTEXT_PATHS.configuration}/api/configurations/products/services`,
        services: `${API_CONTEXT_PATHS.configuration}/api/configurations/services`,
        filteredServices: `${API_CONTEXT_PATHS.configuration}/api/configurations/services/_search`,
        packageTypes: `${API_CONTEXT_PATHS.configuration}/api/configurations/package-types`,
        paymentMethod: `${API_CONTEXT_PATHS.configuration}/api/configurations/payment-method`,
        servicePointProfileConfig: `${API_CONTEXT_PATHS.configuration}/api/configurations/service-point-profile-config`,
        cmpConsentMethod: `${API_CONTEXT_PATHS.configuration}/api/configurations/cmp-consent-method`,
        clusters: `${API_CONTEXT_PATHS.configuration}/api/configurations/clusters`,
        countries: `${API_CONTEXT_PATHS.configuration}/api/configurations/countries`,
        registrationTypes: `${API_CONTEXT_PATHS.configuration}/api/configurations/registration-types`,
        zones: `${API_CONTEXT_PATHS.configuration}/api/configurations/zones`,
        promoCode: `${API_CONTEXT_PATHS.configuration}/api/configurations/promo-codes`,
        notification: `${API_CONTEXT_PATHS.configuration}/api/configurations/notifications`,
        embargo: {
            search: `${API_CONTEXT_PATHS.configuration}/api/configurations/embargo-countries/_search`,
            get: (embrgCtryId: string) =>
                `${API_CONTEXT_PATHS.configuration}/api/configurations/destination-countries/${embrgCtryId}`,
            create: `${API_CONTEXT_PATHS.configuration}/api/configurations/embargo-countries`,
            update: (embrgCtryId: string) =>
                `${API_CONTEXT_PATHS.configuration}/api/configurations/embargo-countries/${embrgCtryId}`,
        },
    },
    consent: {
        search: `${API_CONTEXT_PATHS.consent}/api/consents/_search`,
        push: `${API_CONTEXT_PATHS.consent}/api/consents/_push`,
        transactionSubmit: `${API_CONTEXT_PATHS.consent}/api/consents/transactions/_submit`,
        consentBase: `${API_CONTEXT_PATHS.consent}/api/consents`,
    },
    partial: {
        lookup: `${API_CONTEXT_PATHS.partial}/api/shipments/partial-shipments/_lookup`,
    },
    notification: {
        inbox: `${API_CONTEXT_PATHS.notification}/api/notifications/inbox`,
        markRead: (usrNtfctnInboxId: string) =>
            `${API_CONTEXT_PATHS.notification}/api/notifications/${encodeURIComponent(usrNtfctnInboxId)}/read`,
    },
} as const;

export const API_URLS = {
    sso: {
        redirectUrl: buildApiUrl(API_BASE_URLS.auth, API_PATHS.sso.redirectUrl),
        callbackUrl: buildApiUrl(API_BASE_URLS.auth, API_PATHS.sso.callbackUrl),
        refreshUrl: buildApiUrl(API_BASE_URLS.auth, API_PATHS.sso.refreshUrl),
    },
    customer: {
        tokenGenerate: buildApiUrl(API_BASE_URLS.customer, API_PATHS.customer.tokenGenerate),
        consigneesByCustomer: (custId: string) =>
            buildApiUrl(
                API_BASE_URLS.customer,
                `${API_PATHS.customer.consignees}/customer/${custId}`,
            ),
        consigneesByCountryAndCustomer: (countryCd: string, custId: string) =>
            buildApiUrl(
                API_BASE_URLS.customer,
                `${API_PATHS.customer.consignees}/${countryCd}/customer/${custId}`,
            ),
        consigneeDetail: (cnsgnId: string) =>
            buildApiUrl(API_BASE_URLS.customer, `${API_PATHS.customer.consignees}/${cnsgnId}`),
        updateConsignee: (cnsgnId: string) =>
            buildApiUrl(API_BASE_URLS.customer, `${API_PATHS.customer.consignees}/${cnsgnId}`),
        updateCustomer: (custId: string) =>
            buildApiUrl(API_BASE_URLS.customer, `${API_PATHS.customer.customers}/${custId}`),
    },
    quotation: {
        detail: (qtnId: string) =>
            buildApiUrl(API_BASE_URLS.quotation, `${API_PATHS.quotation.quotationBase}/${qtnId}`),
    },
    configuration: {
        uiLabels: (language: string) =>
            buildApiUrl(
                API_BASE_URLS.configuration,
                `${API_PATHS.configuration.uiLabelsBase}/${language}`,
            ),
        productsByCountry: (ctryCd: string) =>
            buildApiUrl(
                API_BASE_URLS.configuration,
                `${API_PATHS.configuration.products}/${ctryCd}`,
            ),
        servicesByCountry: (ctryCd: string) =>
            buildApiUrl(
                API_BASE_URLS.configuration,
                `${API_PATHS.configuration.services}/${ctryCd}`,
            ),
        tariffById: (trfCd: string) =>
            buildApiUrl(API_BASE_URLS.configuration, `${API_PATHS.configuration.tariffs}/${trfCd}`),
        servicePointById: (srvPntFcId: string) =>
            buildApiUrl(
                API_BASE_URLS.configuration,
                `${API_PATHS.configuration.servicePointConfig}/${encodeURIComponent(srvPntFcId)}`,
            ),
        servicePointProfileById: (srvPntPrflCd: string) =>
            buildApiUrl(
                API_BASE_URLS.configuration,
                `${API_PATHS.configuration.servicePointProfiles}/${srvPntPrflCd}`,
            ),
    },
    consent: {
        transactionByUid: (uid: string) =>
            buildApiUrl(
                API_BASE_URLS.consent,
                `${API_PATHS.consent.consentBase}/transactions/${uid}`,
            ),
        consentByUid: (uid: string) =>
            buildApiUrl(
                API_BASE_URLS.consent,
                `${API_PATHS.consent.consentBase}/${encodeURIComponent(uid)}`,
            ),
        consentStatus: (uid: string) =>
            buildApiUrl(API_BASE_URLS.consent, `${API_PATHS.consent.consentBase}/${uid}/status`),
    },
} as const;

const resolveSsoRedirectUri = (): string => {
    if (SSO_REDIRECT_URI) {
        return SSO_REDIRECT_URI;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}/sso/callback/azure`;
    }
    return 'http://localhost:5173/sso/callback/azure';
};

export const APP_URLS = {
    ssoRedirectUri: resolveSsoRedirectUri(),
} as const;

export { RUNTIME_MODE } from './envConfig';
